import { GroupedByEntityFilterType, ReportRequest } from '@/client/reports';
import { Subjects } from '@/client/users';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { useDepartmentPerformanceReports } from '@/hooks/query/reports.hook';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { AccordionTabHeader } from '@/ui/accordion';
import {
  calculateChartMaxValue,
  createTextWrappingCallback,
  initCustomMiddlePositionForTooltip,
  renderCustomXAxis,
  tooltipDefaultSettings,
} from '@/ui/charts/charts.helper';
import { FlexContainer } from '@/ui/styled-ui';
import {
  DepartmentPerformanceMetrics,
  getDepartmentPerformanceMetricLabel,
  getDepartmentPerformanceMetricOptions,
  getDepartmentsPerformanceChartData,
  getPerformanceChartTitleAndDescription,
  groupedByEntityFilterTypeOptions,
} from '@/utils/helpers/analytics.helper';
import {
  colorBlackMain,
  colorGrayDark,
  colorGrayDarker,
  colorGreenChartMiddle,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from 'primereact/chart';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ChartWrapper = styled(FlexContainer)`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex: 1 1 40%;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  background: var(--white-main);
  align-items: flex-end;
  overflow: hidden;

  .group-selector {
    min-width: 200px;
  }
`;

const ChartContainer = styled.div<{ chartHeight: number }>`
  position: relative;
  width: 100%;
  max-width: 100%;
  max-height: 359px;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  margin-bottom: 40px;

  canvas {
    width: 100% !important;
    height: ${(props) => props.chartHeight}px !important;
  }
`;

const CustomXAxis = styled.div<{ barsStartPosition: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  background: var(--white-main);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: ${(props) => props.barsStartPosition}px;
  font-size: 12px;
`;

type DepartmentPerformanceChartProps = {
  filters: ReportRequest;
};

export const DepartmentPerformanceChart = ({
  filters,
}: DepartmentPerformanceChartProps) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const hasReportingEnabled = canUseFeature(
    Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING,
  );
  const zoomLevel = window.devicePixelRatio || 1;
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [chartHeight, setChartHeight] = useState(200);
  const [barsStartPosition, setBarsStartPosition] = useState(120);
  const chartRef = useRef<any>(null);
  const [selectedMetric, setSelectedMetric] = useState(
    DepartmentPerformanceMetrics.CLICK_RATE,
  );
  const [selectedDepartment, setSelectedDepartment] =
    useState<GroupedByEntityFilterType>(GroupedByEntityFilterType.DEPARTMENT);
  const { departmentPerformance, isLoading } = useDepartmentPerformanceReports(
    currentAccount?.id as string,
    {
      ...filters,
      groupBy: selectedDepartment,
    },
  );

  const hasNoData =
    !departmentPerformance || departmentPerformance.length === 0;

  useEffect(() => {
    initCustomMiddlePositionForTooltip();
  }, []);

  useEffect(() => {
    const docStyle = getComputedStyle(document.documentElement);
    const textColor = colorBlackMain(docStyle);
    const surfaceBorder = colorGrayDark(docStyle);

    const { dataLabels, dataValues } = getDepartmentsPerformanceChartData(
      departmentPerformance,
      selectedMetric,
    );

    const barHeight = 24;
    const barSpacing = 10;
    const minChartHeight = 339;
    const calculatedHeight = Math.max(
      dataLabels.length * (barHeight + barSpacing) + 100,
      minChartHeight,
    );
    setChartHeight(calculatedHeight);

    const cappedDataValues = dataValues.map((value: number) =>
      Math.min(Number(value), 100),
    );

    const data = {
      labels: dataLabels,
      datasets: [
        {
          backgroundColor: colorGreenChartMiddle(docStyle),
          borderColor: colorGreenChartMiddle(docStyle),
          borderDash: [5, 5],
          data: cappedDataValues,
          maxBarThickness: barHeight,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',

      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: true,
          color: colorGrayDarker(docStyle),
          font: {
            size: zoomLevel > 1.25 ? 10 : 12,
            lineHeight: 1,
            family: platformFontFamily(docStyle),
          },
          align: 'right',
          offset: -5,
          formatter: (value: any) => {
            return value === 0 ? 'N/A' : '';
          },
        },
        tooltip: {
          ...tooltipDefaultSettings(),
          xAlign: 'center',
          yAlign: 'bottom',
          position: 'middle',
          intersect: true,
          caretPadding: 10,
          callbacks: {
            title: () => null,
            label: (tooltipItem: any) => {
              const dataIndex = tooltipItem.dataIndex;
              const department = departmentPerformance?.[dataIndex];
              if (!department) return '';

              const rate = tooltipItem.parsed.x;
              const usersCount = department.usersCount;

              const metricLabel = getDepartmentPerformanceMetricLabel(
                t,
                selectedMetric,
              );

              return t('phishing.analytics.department.tooltip', {
                rate: parseFloat(rate.toFixed(2)),
                metric: metricLabel,
                usersCount: usersCount,
              });
            },
          },
        },
      },
      borderRadius: {
        topRight: 5,
        bottomRight: 5,
      },
      scales: {
        x: {
          display: true,
          grid: { display: false },
          border: { display: false },
          min: 0,
          max: calculateChartMaxValue(dataValues),
          position: 'top',
          ticks: {
            display: false,
          },
        },
        y: {
          ticks: {
            color: textColor,
            font: {
              size: zoomLevel > 1.25 ? 10 : 12,
              lineHeight: 1.0,
              family: platformFontFamily(docStyle),
            },
            callback: createTextWrappingCallback(
              departmentPerformance || [],
              false,
              selectedMetric,
            ),
            maxWidth: 150,
            padding: 8,
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false,
            borderDash: [5, 5],
          },
          border: {
            display: false,
            dash: [5, 5],
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [selectedMetric, departmentPerformance, isLoading, filters]);

  useEffect(() => {
    if (!chartData || Object.keys(chartData).length === 0) return;

    const timer = setTimeout(() => {
      if (chartRef.current?.getChart) {
        const chartInstance = chartRef?.current?.getChart();
        if (chartInstance?.chartArea?.left) {
          setBarsStartPosition(chartInstance.chartArea.left);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [chartData, selectedMetric, isLoading, filters]);

  const { name, description } = getPerformanceChartTitleAndDescription(
    t,
    selectedDepartment,
  );

  return (
    <ChartWrapper>
      <FlexContainer direction="column">
        <AccordionTabHeader
          title={t('phishing.analytics.analysis.department', {
            selectedGroup: name,
          })}
          description={description}
        />
        <FlexContainer
          align="flex-start"
          justify="flex-start"
          gap={12}
          className="mt-4 mb-4"
        >
          <Dropdown
            id="department-performance-dropdown"
            options={groupedByEntityFilterTypeOptions(t)}
            value={selectedDepartment}
            onChange={(e: DropdownChangeEvent) =>
              setSelectedDepartment(e.value as GroupedByEntityFilterType)
            }
            className="group-selector"
          />
          <Dropdown
            id="department-performance-metric-dropdown"
            options={getDepartmentPerformanceMetricOptions(t).filter(
              (option) =>
                hasReportingEnabled ||
                option.value !== DepartmentPerformanceMetrics.REPORT_RATE,
            )}
            value={selectedMetric}
            onChange={(e: DropdownChangeEvent) =>
              setSelectedMetric(e.value as string)
            }
          />
        </FlexContainer>
      </FlexContainer>
      <div style={{ flex: 1, display: 'flex', width: '100%' }}>
        {isLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : hasNoData ? (
          <ChartEmptyState
            type={ChartEmptyStateType.PHISHING}
            minHeight={340}
            noBorder
            noMargin
            title={t('phishing.analytics.analysis.department.empty.title')}
            description={t(
              'phishing.analytics.analysis.department.empty.description',
            )}
          />
        ) : (
          <div style={{ position: 'relative', flex: 1 }}>
            <ChartContainer chartHeight={chartHeight}>
              <Chart
                ref={chartRef}
                id="department-performance-chart"
                type="bar"
                data={chartData}
                options={chartOptions}
                plugins={[ChartDataLabels]}
              />
            </ChartContainer>
            <CustomXAxis barsStartPosition={barsStartPosition}>
              {renderCustomXAxis(departmentPerformance, false, selectedMetric)}
            </CustomXAxis>
          </div>
        )}
      </div>
    </ChartWrapper>
  );
};
